import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { FaPrint, FaTrashAlt } from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import Input from '../../components/Input';
import SelectSync from '../../components/SelectSync';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';
import Paginacao from '../../components/Paginacao';
import PedidoExameAgendamento from '../../components/PedidoExameAgendamento';
import Agendamento from '../../components/Agendamento';

import getValidationErrors from '../../utils/getValidationErrors';

import { PacienteLinha, PacienteLista } from './styles';

import getResponseErrors from '../../utils/getResponseErrors';
import { usePerfil } from '../../hooks/perfil';
import { IPedidoEspecialidade } from '../PacienteAtendimento/PedidoEncaminhamento';

interface IFormBusca {
  PacNome: string;
  PedPerCodigo: number;
  PedFinalizado: string;
}

interface IPaginacao {
  PacNome?: string;
  PedPerCodigo?: number;
  PedFinalizado?: string;
  limite: number;
  pagina: number;
}

interface IAgendamentoSelecionado {
  AgeCodigo: number;
  AgeConfCodigo: number;
}

export interface IOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

const PedidoEspecialidadeBusca: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsPerfil, buscaPerfil } = usePerfil();
  const history = useHistory();
  const formRefBusca = useRef<FormHandles>(null);
  const [
    pedidoEncaminhamentoSelecionado,
    setPedidoEncaminhamentoSelecionado,
  ] = useState<number | undefined>();
  const [pedidoEspecialidade, setPedidoEspecialidade] = useState<
    IPedidoEspecialidade[] | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [showPedidoEncaminhamento, setShowPedidoEncaminhamento] = useState(
    false,
  );
  const [showAgendamento, setShowAgendamento] = useState(false);
  const [
    agendamentoSelecionado,
    setAgendamentoSelecionado,
  ] = useState<IAgendamentoSelecionado>();
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    async function loadPerfis(): Promise<void> {
      await buscaPerfil();
    }
    if (!optionsPerfil || Object.keys(optionsPerfil).length === 0) loadPerfis();
  }, [buscaPerfil, optionsPerfil]);

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const initialData = useMemo(() => {
    const PedPerCodigo = query.get('perfil') || 0;
    const PedFinalizado = query.get('status') || 'false';

    return { PedPerCodigo, PedFinalizado };
  }, [query]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        PacNome: Yup.string(),
        PedPerCodigo: Yup.number()
          .nullable()
          .transform(x => x || null),
        PedFinalizado: Yup.boolean(),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setPedidoEspecialidade(undefined);

      const { PacNome, PedPerCodigo, PedFinalizado } = dados;

      setPaginacao({
        ...(PacNome.toString().length > 1 ? { PacNome } : {}),
        ...(PedPerCodigo.toString().length > 0
          ? { PedPerCodigo: Number(PedPerCodigo) }
          : {}),
        ...(PedFinalizado.toString().length > 0 ? { PedFinalizado } : {}),
        pagina: 1,
        limite: 20,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  useEffect(() => {
    formRefBusca.current?.submitForm();
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;

        let parametros = `&PedCliCodigo=${user.UsuClinica}`;
        const { PacNome, PedFinalizado, PedPerCodigo } = paginacao;
        if (PacNome) parametros += `&PedPacNome=${PacNome}`;
        if (PedFinalizado) parametros += `&PedFinalizado=${PedFinalizado}`;
        if (PedPerCodigo) parametros += `&PedPerCodigo=${PedPerCodigo}`;

        const response = await api.get(
          `/pedidoEspecialidade?&pagina=${paginacao.pagina}&limite=${paginacao.limite}${parametros}`,
        );
        const responseApi: IPedidoEspecialidade[] = response.data[0];

        setTotalRegistros(
          response.data[1] === 0 ? undefined : response.data[1],
        );

        const PedidoExamesFormatados = responseApi.map(ped => {
          return {
            ...ped,
            dataFormatada: format(
              parseISO(ped.created_at.toString()),
              'dd/MM/yyyy HH:mm',
            ),
            statusFormatado:
              ped.PedFinalizado === true ? 'Finalizado' : 'Em Aberto',
          };
        });

        setPedidoEspecialidade(PedidoExamesFormatados);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar Pedido de Exames',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao, user.UsuClinica]);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const handleDelete = useCallback(
    async (codigo: number) => {
      try {
        setLoading(true);

        await api.delete(`/pedidoEspecialidade/${codigo}`);
        setPaginacao(state => {
          return state ? { ...state, dataFake: new Date() } : undefined;
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleFinalizouPedido = useCallback(
    (dados: IAgendamentoSelecionado) => {
      setPedidoEncaminhamentoSelecionado(undefined);
      setShowPedidoEncaminhamento(false);
      setPaginacao(state => {
        return state ? { ...state, dataFake: new Date() } : undefined;
      });
      setAgendamentoSelecionado(dados);
      setShowAgendamento(true);
    },
    [],
  );

  const handleFecharAgendamento = useCallback((dados: any) => {
    setAgendamentoSelecionado(undefined);
    setShowAgendamento(false);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      {showPedidoEncaminhamento && pedidoEncaminhamentoSelecionado && (
        <Modal
          show={showPedidoEncaminhamento}
          titulo={`Pedido de Encaminhamento Nº ${pedidoEncaminhamentoSelecionado}`}
          handleFecharModal={
            () => {
              setPedidoEncaminhamentoSelecionado(undefined);
              setShowPedidoEncaminhamento(false);
            }
            // eslint-disable-next-line react/jsx-curly-newline
          }
        >
          <PedidoExameAgendamento
            PedCodigo={pedidoEncaminhamentoSelecionado}
            handleFechar={dados => handleFinalizouPedido(dados)}
            tipoExame={false}
          />
        </Modal>
      )}

      {showAgendamento && agendamentoSelecionado && (
        <Modal
          show={showAgendamento}
          titulo="Agendamento"
          handleFecharModal={
            () => {
              setAgendamentoSelecionado(undefined);
              setShowAgendamento(false);
            }
            // eslint-disable-next-line react/jsx-curly-newline
          }
        >
          <Agendamento
            agendaId={agendamentoSelecionado.AgeConfCodigo}
            AgeCodigo={agendamentoSelecionado.AgeCodigo}
            handleFecharAgendamento={handleFecharAgendamento}
          />
        </Modal>
      )}
      <div className="mb-2 p-2 bg-white border rounded">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 id="topoBusca">Pesquisa Pedido de Exames</h6>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => history.push('/agendas')}
          >
            Voltar para agendas
          </Button>
        </div>

        <Form
          ref={formRefBusca}
          initialData={initialData}
          onSubmit={handleSubmitBusca}
        >
          <Row noGutters>
            <Col sm={12} md={5} lg={3} className="pr-1">
              <Input
                name="PacNome"
                label="Nome"
                maxLength={100}
                minLength={3}
              />
            </Col>
            <Col sm={12} md={4} lg={2} className="pr-1">
              <SelectSync
                name="PedPerCodigo"
                label="Perfil"
                isClearable
                handleSelectChange={handleOnChangeVazio}
                options={optionsPerfil.optionPerfis}
              />
            </Col>
            <Col sm={6} md={4} lg={2} className="pr-1">
              <SelectSync
                name="PedFinalizado"
                label="Status"
                handleSelectChange={handleOnChangeVazio}
                options={[
                  { label: 'Finalizado', value: 'true' },
                  { label: 'Aguardando Agendamento', value: 'false' },
                ]}
              />
            </Col>
            <Col sm={12} md={3} lg={1} className="pr-1">
              <Button
                type="submit"
                size="sm"
                className="mt-3"
                variant="primary"
              >
                BUSCAR
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="bg-white border p-2 rounded">
        {pedidoEspecialidade ? (
          <>
            <Row noGutters className="bg-light py-2 border-top border-bottom">
              <Col md={1} lg={1}>
                <b>Data/Hora</b>
              </Col>
              <Col md={2} lg={3}>
                <b>Paciente</b>
              </Col>
              <Col md={2} lg={2}>
                <b>Médico</b>
              </Col>
              <Col md={2} lg={2}>
                <b>Perfil</b>
              </Col>
              <Col md={2} lg={2}>
                <b>Status</b>
              </Col>
              <Col md={2} lg={1} />
              <Col md={1} lg={1} />
            </Row>
            {pedidoEspecialidade.map(ped => (
              <PacienteLinha
                className="row no-gutters"
                key={`ped-${ped.PedCodigo}`}
                data-id={ped.PedCodigo}
              >
                <div className="col-md-1 col-lg-1">
                  <PacienteLista>{ped.dataFormatada}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-3">
                  <PacienteLista>{ped.paciente.PacNome}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-2">
                  <PacienteLista>{ped.medico.UsuNome}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-2">
                  <PacienteLista>{ped.PedEspecialidade}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-2">
                  <PacienteLista>{ped.statusFormatado}</PacienteLista>
                </div>

                <div
                  className={`col-md-2 col-lg-1 ${
                    ped.PedFinalizado === true ? 'd-none' : ''
                  }`}
                >
                  <PacienteLista>
                    {ped.PedPerCodigo && (
                      <Button
                        type="button"
                        variant="outline-success"
                        size="sm"
                        data-id={ped.PedCodigo}
                        onClick={() => {
                          setPedidoEncaminhamentoSelecionado(ped.PedCodigo);
                          setShowPedidoEncaminhamento(true);
                        }}
                      >
                        Agendar
                      </Button>
                    )}
                  </PacienteLista>
                </div>
                <div className="col-md-1 col-lg-1">
                  <PacienteLista>
                    {!ped.PedFinalizado ? (
                      <>
                        <Link
                          className="btn btn-sm btn-outline-secondary mr-1"
                          to={`/impressaodocumento/${ped.PedCodigo}/pedidoencaminhamentonaoagendado`}
                          target="_blank"
                          title="Imprimir pedido de exame"
                        >
                          <FaPrint size={14} />
                        </Link>

                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          data-id={ped.PedCodigo}
                          onClick={() => handleDelete(ped.PedCodigo)}
                        >
                          <FaTrashAlt size={14} />
                        </Button>
                      </>
                    ) : (
                      <Link
                        className="btn btn-sm btn-outline-secondary mr-1"
                        to={`/impressaodocumento/${ped.PedAgeCodigoGerado}/pedidoencaminhamento`}
                        target="_blank"
                        title="Imprimir encaminhamento"
                      >
                        <FaPrint size={14} />
                      </Link>
                    )}
                  </PacienteLista>
                </div>
              </PacienteLinha>
            ))}
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Pesquise pelo nome do paciente, Pelo nome do médico, status ou
              perfil.
            </h5>
          </div>
        )}

        <Paginacao
          limitePagina={paginacao?.limite || 20}
          paginasVizinhas={2}
          totalRegistros={totalRegistros}
          paginaInicio={paginacao?.pagina}
          handleProximaPagina={handleProximaPagina}
        />
      </div>
    </Container>
  );
};

export default PedidoEspecialidadeBusca;
